import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Download,
  ContentCopy,
  CheckCircle,
  ExpandMore,
  Speed,
  Timer
} from '@mui/icons-material';
import { ProcessingResult } from '../../services/document';

interface ResultDisplayProps {
  result: ProcessingResult;
  onStartNew: () => void;
}

const ResultDisplay: React.FC<ResultDisplayProps> = ({ result, onStartNew }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyContent = async () => {
    try {
      await navigator.clipboard.writeText(result.redacted_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([result.redacted_content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `redacted_${result.original_filename.replace(/\.[^/.]+$/, "")}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <CheckCircle color="success" sx={{ fontSize: 48, mb: 1 }} />
        <Typography variant="h4" color="success.main" gutterBottom>
          Processing Complete!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your CV has been successfully redacted and is ready for download.
        </Typography>
      </Box>

      {/* Statistics Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center' }}>
            <Timer color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h6">Processing Time</Typography>
            <Typography variant="h6" color="primary.main">
              {result.processing_time.toFixed(1)}s
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center' }}>
            <Speed color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h6">Document</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {result.original_filename}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Content Preview */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Preview Redacted Content</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
            <pre style={{
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              lineHeight: 1.5,
              margin: 0
            }}>
              {result.redacted_content}
            </pre>
          </Paper>
        </AccordionDetails>
      </Accordion>

      <Divider sx={{ my: 3 }} />

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Download />}
          onClick={handleDownload}
          sx={{ minWidth: 150 }}
        >
          Download Markdown
        </Button>

        <Button
          variant="outlined"
          size="large"
          startIcon={<ContentCopy />}
          onClick={handleCopyContent}
          sx={{ minWidth: 150 }}
        >
          {copied ? 'Copied!' : 'Copy Content'}
        </Button>

        <Button
          variant="outlined"
          size="large"
          onClick={onStartNew}
          sx={{ minWidth: 150 }}
        >
          Process Another CV
        </Button>
      </Box>

      {/* Footer Info */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Document ID: {result.document_id}
        </Typography>
      </Box>
    </Box>
  );
};

export default ResultDisplay;
